import React, { useRef } from 'react';
import clsx from 'clsx';
import {
  AriaDateFieldOptions,
  useDateField,
  DateValue,
} from 'react-aria/useDateField';
import { DateSegment } from './DateSegment';
import { useLocale } from 'react-aria/I18nProvider';
import { useMessageFormatter } from '@react-aria/i18n';
import {
  SegmentType,
  useDateFieldState,
} from 'react-stately/useDateFieldState';
import { createCalendar } from '@internationalized/date';
import styles from '@danske/sapphire-css/components/dateField/dateField.module.css';
import { CloseOutline } from '@danske/sapphire-icons/react';
import { DateFieldButton } from './DateFieldButton';
import intlMessages from '../i18n';
import { segmentTypes, isAnyModified } from './utils/segments';
import { useCustomDateFormatLocale } from '../../SapphireProvider';
import { Icon } from '../../Icon';
import { CalendarGranularity } from '../../Calendar/src/useCalendarState';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from 'react-aria/filterDOMProps';

export const allowedSegments: Record<CalendarGranularity, SegmentType[]> = {
  day: ['day', 'month', 'year'],
  month: ['month', 'year'],
  year: ['year'],
};

export type DateInputProps<T extends DateValue> = Omit<
  AriaDateFieldOptions<T>,
  'granularity'
> &
  GlobalDomAttributes & {
    noClearButton?: boolean;
    size?: 'lg' | 'md';
    granularity?: CalendarGranularity;
    alignInputRight?: boolean;
  };

/**
 * @internal
 */
export function DateInput<T extends DateValue>({
  noClearButton = false,
  size = 'lg',
  granularity = 'day',
  alignInputRight,
  ...props
}: DateInputProps<T>): React.JSX.Element {
  const ref = useRef(null);
  const formatMessage = useMessageFormatter(intlMessages);
  const { locale } = useLocale();
  const customDateFormatLocale = useCustomDateFormatLocale();

  const state = useDateFieldState({
    ...props,
    // @ts-expect-error   Type '"month"' is not assignable to type 'Granularity | undefined'
    // We need to pass the granularity to the state to trigger onChange
    // when not all segments are filled
    granularity,
    locale: customDateFormatLocale || locale,
    createCalendar,
  });

  const { fieldProps } = useDateField(props, state, ref);
  const clear = () => {
    segmentTypes.forEach((type) => state.clearSegment(type));
    // After the refactoring in @react-stately/datepicker v3.16.0, calling clearSegment
    // alone isn't sufficient. We need to explicitly set the value to null.
    // See similar workaround in DateRangeInput.tsx
    state.setValue(null!);
  };

  const segments = state.segments.reduce((result, segment, i, arr) => {
    if (segment.type === 'literal') {
      // Only include literal if the previous segment is allowed
      if (i === 0) return result;
      const prev = arr[i - 1];
      if (allowedSegments[granularity]?.includes(prev.type)) {
        result.push(segment);
      }
    } else if (allowedSegments[granularity]?.includes(segment.type)) {
      result.push(segment);
    }
    return result;
  }, [] as typeof state.segments);

  const showClearButton =
    !noClearButton &&
    !props.isReadOnly &&
    !props.isDisabled &&
    isAnyModified(state.segments);

  const clearButton = showClearButton ? (
    <DateFieldButton
      aria-label={formatMessage('clear')}
      onPress={clear}
      isClearButton
    >
      <Icon size={size === 'md' ? 'sm' : 'md'}>
        <CloseOutline />
      </Icon>
    </DateFieldButton>
  ) : null;

  return (
    <>
      {alignInputRight ? clearButton : null}
      <div
        {...fieldProps}
        {...filterDOMProps(props, { global: true })}
        className={clsx(styles['sapphire-date-field__input'], {
          [styles['sapphire-date-field__input--align-right']]:
            !!alignInputRight,
        })}
        ref={ref}
      >
        {segments.map((segment, i) => (
          <DateSegment key={i} segment={segment} state={state} />
        ))}
      </div>
      {!alignInputRight ? clearButton : null}
    </>
  );
}
